import { i18n } from './i18n/i18n';
import type { TranslationKey } from './i18n/dictionaries';

// Helpers para crear elementos DOM (escape automático vía textContent)

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: {
    className?: string;
    text?: string;
    onClick?: (e: Event) => void;
    attrs?: Record<string, string>;
  } = {},
  children: (HTMLElement | string)[] = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (props.className) node.className = props.className;
  if (props.text !== undefined) node.textContent = props.text;
  if (props.onClick) node.addEventListener('click', props.onClick);
  if (props.attrs) {
    for (const [k, v] of Object.entries(props.attrs)) {
      node.setAttribute(k, v);
    }
  }
  for (const child of children) {
    if (typeof child === 'string') {
      node.appendChild(document.createTextNode(child));
    } else {
      node.appendChild(child);
    }
  }
  return node;
}

export function clear(node: HTMLElement) {
  node.replaceChildren();
}

export function showToast(message: string, type: 'success' | 'error' | 'info' = 'info') {
  const existing = document.querySelector('.toast');
  if (existing) existing.remove();

  const toast = el('div', {
    className: `toast ${type}`,
    text: message,
    attrs: { role: 'status' },
  });
  document.body.appendChild(toast);
  setTimeout(() => toast.remove(), 4000);
}

export function showConfirm(message: string): Promise<boolean> {
  return new Promise((resolve) => {
    const backdrop = el('div', { className: 'modal-backdrop' });
    const modal = el('div', { className: 'modal' });
    const title = el('h3', { text: i18n.t('common.confirm') });
    const text = el('p', { text: message });
    const actions = el('div', { className: 'modal-actions' });
    const cancelBtn = el('button', {
      className: 'btn btn-secondary',
      text: i18n.t('common.cancel'),
      onClick: () => { backdrop.remove(); resolve(false); },
    });
    const okBtn = el('button', {
      className: 'btn btn-primary',
      text: i18n.t('common.accept'),
      onClick: () => { backdrop.remove(); resolve(true); },
    });
    actions.append(cancelBtn, okBtn);
    modal.append(title, text, actions);
    backdrop.appendChild(modal);
    document.body.appendChild(backdrop);
  });
}

export function showPrompt(message: string, options?: { multiline?: boolean; required?: boolean; minLength?: number }): Promise<string | null> {
  return new Promise((resolve) => {
    const backdrop = el('div', { className: 'modal-backdrop' });
    const modal = el('div', { className: 'modal' });
    const title = el('h3', { text: i18n.t('common.confirm') });
    const text = el('p', { text: message });
    const formGroup = el('div', { className: 'form-group' });

    const input = options?.multiline
      ? document.createElement('textarea')
      : document.createElement('input');
    if (input instanceof HTMLInputElement) input.type = 'text';
    if (input instanceof HTMLTextAreaElement) input.rows = 4;
    input.style.width = '100%';

    formGroup.appendChild(input);

    const actions = el('div', { className: 'modal-actions' });
    const cancelBtn = el('button', {
      className: 'btn btn-secondary',
      text: i18n.t('common.cancel'),
      onClick: () => { backdrop.remove(); resolve(null); },
    });
    const okBtn = el('button', {
      className: 'btn btn-primary',
      text: i18n.t('common.accept'),
      onClick: () => {
        const value = input.value.trim();
        if (options?.required && !value) {
          showToast(i18n.t('common.requiredField'), 'error');
          return;
        }
        if (options?.minLength && value.length < options.minLength) {
          showToast(i18n.t('common.minChars', { n: options.minLength }), 'error');
          return;
        }
        backdrop.remove();
        resolve(value);
      },
    });
    actions.append(cancelBtn, okBtn);
    modal.append(title, text, formGroup, actions);
    backdrop.appendChild(modal);
    document.body.appendChild(backdrop);
    setTimeout(() => input.focus(), 50);
  });
}

export function getISOWeek(date: Date): { week: number; year: number } {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const week = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return { week, year: d.getUTCFullYear() };
}

export function statusLabel(status: string): string {
  const key = `status.${status}` as TranslationKey;
  return i18n.t(key);
}

/** Traduce el código de unidad de la BD al idioma actual */
export function unitLabel(unit: string): string {
  const key = `unit.${unit}` as TranslationKey;
  const translated = i18n.t(key);
  // Si no hay traducción, devolver el valor original
  return translated === key ? unit : translated;
}

/** Helper de traducción para usar en componentes */
export function t(key: TranslationKey, params?: Record<string, string | number>): string {
  return i18n.t(key, params);
}
